/**
 * Events Service
 * 
 * Service for fetching work order events with caching mechanisms
 * for improved performance.
 */

import { apiClient } from '../api';
import { WorkOrderEvent, JobType, WorkOrderStatus, PriorityLevel, DateRange } from './types';

// Cache configuration
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes in milliseconds

// Cache structure
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// In-memory cache
const eventsCache: Record<string, CacheEntry<WorkOrderEvent[]>> = {};

/**
 * Generates a cache key for events
 */
const getCacheKey = (site: string, equipmentId: string, limit?: number, dateRange?: DateRange): string => {
  const dateRangeKey = dateRange ? `${dateRange.startDate}_${dateRange.endDate}` : 'all';
  return `events_${site}_${equipmentId}_${limit || 'all'}_${dateRangeKey}`;
};

/**
 * Checks if a cache entry is still valid
 */
const isCacheValid = (entry: CacheEntry<any>): boolean => {
  return Date.now() - entry.timestamp < CACHE_TTL;
};

/**
 * Events Service with methods for fetching work order events
 */
export const eventsService = {
  /**
   * Fetches work order events for a specific equipment ID
   * with caching for improved performance
   */
  getWorkOrderEvents: async (
    site: string,
    equipmentId: string, 
    dateRange?: DateRange,
    limit?: number
  ): Promise<WorkOrderEvent[]> => {

    const cacheKey = getCacheKey(site, equipmentId, limit, dateRange);
    
    // Check cache first
    const cachedData = eventsCache[cacheKey];
    if (cachedData && isCacheValid(cachedData)) {
      return cachedData.data;
    }
    
    // Fetch from API if not in cache or cache expired
    const params: Record<string, string | number | boolean | undefined> = {
      site: encodeURIComponent(site),
      eq_id: encodeURIComponent(equipmentId),
    };
    
    if (limit) {
      params.limit = limit;
    }
    if (dateRange) {
      params.start_date = dateRange.startDate;
      params.end_date = dateRange.endDate;
    }
    
    const data = await apiClient.get<WorkOrderEvent[]>(
      '/work-orders',
      { params }
    );
    
    // Update cache
    eventsCache[cacheKey] = {
      data,
      timestamp: Date.now()
    };
    
    return data;
  },
  
  /**
   * Fetches work order events filtered by job type
   */
  getWorkOrderEventsByJobType: async (
    site: string,
    equipmentId: string, 
    jobType: JobType,
    dateRange?: DateRange,
    limit?: number
  ): Promise<WorkOrderEvent[]> => {
    const params: Record<string, string | number | boolean | undefined> = {
      jobType,
      site: encodeURIComponent(site),
      eq_id: encodeURIComponent(equipmentId),
    };    

    if (limit) {
      params.limit = limit;
    }
    
    if (dateRange) {
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    }
    
    return apiClient.get<WorkOrderEvent[]>(
      '/work-orders',
      { params }
    );
  },
  
  /**
   * Fetches work order events filtered by status
   */
  getWorkOrderEventsByStatus: async (
    site: string,
    equipmentId: string, 
    status: WorkOrderStatus,
    dateRange?: DateRange,
    limit?: number
  ): Promise<WorkOrderEvent[]> => {
    const params: Record<string, string | number | boolean | undefined> = {
      status,
      site: encodeURIComponent(site),
      eq_id: encodeURIComponent(equipmentId),
    };
    
    if (limit) {
      params.limit = limit;
    }
    
    if (dateRange) {
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    }
    
    return apiClient.get<WorkOrderEvent[]>(
      '/work-orders',
      { params }
    );
  },
  
  /**
   * Fetches work order events filtered by priority level
   */
  getWorkOrderEventsByPriority: async (
    site: string,
    equipmentId: string, 
    priority: PriorityLevel,
    dateRange?: DateRange,
    limit?: number
  ): Promise<WorkOrderEvent[]> => {
    const params: Record<string, string | number | boolean | undefined> = {
      priority,
      site: encodeURIComponent(site),
      eq_id: encodeURIComponent(equipmentId),
    };
    
    if (limit) {
      params.limit = limit;
    }
    
    if (dateRange) {
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    }
    
    return apiClient.get<WorkOrderEvent[]>(
      '/work-orders',
      { params }
    );
  },
  
  /**
   * Fetches work order events for a date range
   * @deprecated Use getWorkOrderEvents with dateRange parameter instead
   */
  getWorkOrderEventsByDateRange: async (
    site: string,
    equipmentId: string,
    startDate: Date,
    endDate: Date,
    limit?: number
  ): Promise<WorkOrderEvent[]> => {
    const dateRange: DateRange = {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
    
    return eventsService.getWorkOrderEvents(site, equipmentId, dateRange, limit);
  },
  
  /**
   * Clears the events cache for a specific equipment ID
   * or the entire cache if no parameters are provided
   */
  clearCache: (site?: string, equipmentId?: string): void => {
    if (site && equipmentId) {
      // Clear cache for specific equipment ID
      Object.keys(eventsCache).forEach(key => {
        if (key.startsWith(`events_${site}_${equipmentId}`)) {
          delete eventsCache[key];
        }
      });
    } else if (site) {
      // Clear cache for specific equipment ID
      Object.keys(eventsCache).forEach(key => {
        if (key.startsWith(`events_${site}`)) {
          delete eventsCache[key];
        }
      });
    } else {
      // Clear all cache
      Object.keys(eventsCache).forEach(key => {
        delete eventsCache[key];
      });
    }
  }
};